import dayjs from 'dayjs';
import durationPlugin from 'dayjs/plugin/duration';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Button from '@/components/Button';
import TooltipLarge from '@/components/TooltipLarge';
import { DOCUMENT_TTL_HOURS, STORAGE_DAYS } from '@/config';
import { useMediaQuery } from '@/hooks';
import { useHasStorageCharge, useIsTrialing } from '@/hooks/user';
import Clock from '@/icons/clock.svg';
import { BILLING_TAB } from '@/pages/Account/constants';
import routes from '@/routes';
import { setIsModalTimerOpen } from '@/store/myDocuments';

dayjs.extend(durationPlugin);

function Timer({ createdAt, tooltip = true }) {
	const dispatch = useDispatch();
	const isTrialing = useIsTrialing();
	const mdDown = useMediaQuery('mdDown');
	const navigate = useNavigate();
	const hasStorageCharge = useHasStorageCharge();
	const { t } = useTranslation();

	// get state to re-render every minute
	useSelector(state => state.myDocuments.minutes);

	if (!createdAt) {
		return null;
	}

	const ONE_DAY_IN_MINUTES = 24 * 60;
	const endOfLife = hasStorageCharge
		? dayjs.unix(createdAt).add(STORAGE_DAYS, 'days')
		: dayjs.unix(createdAt).add(DOCUMENT_TTL_HOURS, 'hours');
	const timeLeftDays = endOfLife.diff(dayjs(), 'days');
	const timeLeftMinutes = endOfLife.diff(dayjs(), 'minutes');
	const isMoreThanOneDay = timeLeftMinutes > ONE_DAY_IN_MINUTES;
	const duration = isMoreThanOneDay
		? `${timeLeftDays}d`
		: dayjs.duration(Math.max(timeLeftMinutes, 0), 'minutes').format('HH:mm');

	const content = (
		<div className="flex h-8 items-center gap-1 rounded-sm bg-grey-100 px-2 text-[10px] font-semibold leading-none text-gray-500 md:h-6 md:px-1">
			<Clock className="h-[14px] w-[14px]" />
			<span className="mt-[1px] inline-block">{duration}</span>
		</div>
	);

	if (tooltip) {
		return mdDown ? (
			<Button
				className="p-0"
				onClick={() => {
					dispatch(setIsModalTimerOpen(true));
				}}
				variant="ghost"
			>
				{content}
			</Button>
		) : (
			<TooltipLarge
				title={t('myDocuments.timerTitle')}
				description={
					hasStorageCharge
						? t('myDocuments.timerDescriptionStorage', {
								storageDays: STORAGE_DAYS
							})
						: t('myDocuments.timerDescription')
				}
				actionText={t('myDocuments.timerAction')}
				onClick={() =>
					navigate(routes.myAccount, {
						state: {
							openChangePlanModal: isTrialing,
							currentTab: BILLING_TAB
						}
					})
				}
			>
				{content}
			</TooltipLarge>
		);
	}

	return content;
}

export default Timer;
